/**
 * Welcome screen component shown while connecting to VoiceLive
 */

import React from 'react';
import { AgeProfile } from '../config/ageProfiles.config';
import { VoiceLiveConnectionState } from '../types/voiceLive.types';
import ConfigurationHelp from './ConfigurationHelp';
import { CheckCircleIcon } from './SvgConsolidated/SvgIcons';

interface WelcomeScreenProps {
  ageProfile: AgeProfile;
  connectionState: VoiceLiveConnectionState;
  onRetry: () => void;
  errorMessage?: string;  // Add optional error message prop
}

export default function WelcomeScreen({ ageProfile, connectionState, onRetry, errorMessage }: WelcomeScreenProps) {
  const getStatusMessage = () => {
    switch (connectionState) {
      case 'connecting':
        return ageProfile.content.statusMessages.connecting;
      case 'error':
        // Use custom error message if provided, otherwise use default
        return errorMessage || ageProfile.content.statusMessages.error;
      default:
        return 'Initializing...';
    }
  };

  const getStatusIcon = () => {
    switch (connectionState) {
      case 'connecting':
        return (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        );
      case 'error':
        return (
          <div className="h-12 w-12 text-red-500">
            <CheckCircleIcon className="w-12 h-12" />
          </div>
        );
      default:
        return (
          <div className="h-12 w-12 text-gray-400">
            <CheckCircleIcon className="w-12 h-12" />
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col items-center max-w-4xl w-full space-y-4">
      {/* Show configuration help if error is related to Azure configuration */}
      {connectionState === 'error' && errorMessage?.includes('Azure') && (
        <ConfigurationHelp className="w-full" />
      )}
      
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <h1 className={`${ageProfile.theme.typography.headingSize} font-bold mb-4`} style={{ color: ageProfile.theme.colors.primary }}>
            {ageProfile.ui.appName}
          </h1>
          <p className={`${ageProfile.theme.typography.bodySize} text-gray-600 mb-6`}>
            {ageProfile.content.welcomeMessage}
          </p>
        </div>

      <div className="flex flex-col items-center mb-6">
        {getStatusIcon()}
        <div className={`${ageProfile.theme.typography.bodySize} mt-4 text-gray-700 max-w-md`}>
          {/* Support multiline error messages */}
          {connectionState === 'error' && errorMessage ? (
            <div className="text-left space-y-2">
              {errorMessage.split('\n').map((line, index) => (
                <p key={index} className={line.startsWith('•') ? 'ml-4' : ''}>
                  {line}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-center">{getStatusMessage()}</p>
          )}
        </div>
      </div>

      {connectionState === 'error' && (
        <div className="space-y-3">
          {/* Show different action based on error type */}
          {errorMessage?.includes('Azure') || errorMessage?.includes('Server Configuration Required') ? (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-semibold mb-1">⚙️ Configuration Required</p>
                <p className="text-xs">After configuring Azure credentials, restart the backend server and refresh this page.</p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className={`w-full ${ageProfile.theme.spacing.buttonPadding} ${ageProfile.theme.borderRadius} font-semibold text-white transition-all duration-200 transform hover:scale-105`}
                style={{ backgroundColor: ageProfile.theme.colors.primary }}
              >
                🔄 Refresh Page
              </button>
            </>
          ) : (
            <button
              onClick={onRetry}
              className={`w-full ${ageProfile.theme.spacing.buttonPadding} ${ageProfile.theme.borderRadius} font-semibold text-white transition-all duration-200 transform hover:scale-105`}
              style={{ backgroundColor: ageProfile.theme.colors.primary }}
            >
              🔄 Try Again
            </button>
          )}
          
          {/* Add link to Teacher Dashboard as alternative */}
          <a
            href="/dashboard"
            className={`block w-full ${ageProfile.theme.spacing.buttonPadding} ${ageProfile.theme.borderRadius} font-semibold text-center border-2 transition-all duration-200 transform hover:scale-105`}
            style={{ 
              borderColor: ageProfile.theme.colors.primary,
              color: ageProfile.theme.colors.primary
            }}
          >
            📊 Go to Teacher Dashboard
          </a>
          
          <p className="text-xs text-gray-500 text-center mt-2">
            Voice features unavailable • Other features still accessible
          </p>
        </div>
      )}

      {/* Only show instructions when not in error state */}
      {connectionState !== 'error' && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            {ageProfile.content.instructions}
          </p>
        </div>
      )}
      </div>
    </div>
  );
}